/**
 * spriteCdn — the single source of truth for where Pokémon artwork is loaded
 * from at runtime.
 *
 * The app previously hotlinked artwork from three third-party hosts: the
 * PokeAPI sprites repo and the pokesprite repo (both on GitHub raw), plus
 * pokemondb's image host. That made every dex cell a live dependency on
 * somebody else's repo: their outage was our outage, an upstream path reorg
 * broke production with no deploy on our side, and it served our entire art
 * layer off GitHub's CDN. `scripts/ip-check.sh` fails the build if any of
 * those hosts reappear in the bundle — including from a comment like this
 * one, which is why they're named in prose here rather than spelled out.
 *
 * Those hosts are now a BUILD-TIME SOURCE ONLY: `scripts/sync-sprites-to-r2.ts`
 * mirrors every asset we render into our own R2 bucket (nexusdex-sprites),
 * served from sprites.nexusdex.ai. Upstream paths are preserved under a
 * per-source prefix, so adding art means running the sync script — never
 * pointing a URL back at a third party.
 *
 * If you are adding a new artwork source: mirror it in the sync script and add
 * a prefix here. Do NOT introduce a new external host.
 */

/** Public base for our own mirrored artwork. No trailing slash. */
export const SPRITE_CDN_BASE = "https://sprites.nexusdex.ai";

/**
 * Is this URL a Pokémon GO game icon on our mirror: a PokeMiners export
 * (`pokeminers/`) or a hand-made cut-out for a costume PokeMiners has not
 * exported yet (`curated/`, see CURATED in scripts/manualFormOverrides.ts)?
 * Both come on a padded canvas with the subject off-centre, so both take the
 * measured display scale from scripts/measure-form-sprite-scales.ts. HOME and
 * pixel sprites fill their canvas and never do.
 */
export function isGoIconSpriteUrl(url: string): boolean {
  return /\/(?:pokeminers|curated)\//.test(url);
}

/** Mirror of the PokeAPI `sprites/pokemon` tree (base, shiny, other/*). */
export const SPRITE_CDN_POKEAPI = `${SPRITE_CDN_BASE}/pokeapi`;

/** Mirror of the PokeAPI `sprites/pokemon/other` tree (official-artwork, home). */
export const SPRITE_CDN_POKEAPI_OTHER = `${SPRITE_CDN_POKEAPI}/other`;

/** Mirror of the pokesprite repo root (pokemon-gen8/, pokemon-gen7x/). */
export const SPRITE_CDN_POKESPRITE = `${SPRITE_CDN_BASE}/pokesprite`;


